import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import { apiFetch } from '../../lib/api';
import type { IsoDate } from '../../lib/dates';
import { dmy } from './types';

/**
 * Send a fortnight to Xero for several staff, one after another.
 *
 * The legacy screen sent one person per visit: pick a staff member, open Total,
 * press Send To Xero, go back, pick the next. For a fortnight that is fifteen
 * round trips through the same three screens, and nothing anywhere showed who
 * had already been done — the only record was the button changing to "Uploaded
 * To Xero" once you navigated back to that person.
 *
 * ── WHY IT IS SEQUENTIAL, NOT PARALLEL ──────────────────────────────────────
 * Each send is several Xero calls (a draft timesheet, then one leave
 * application per leave day), all against the same tenant with one OAuth token.
 * Firing fifteen of those at once invites rate limiting, and a 429 halfway
 * through leaves a pay run half sent with no clear record of where it stopped.
 * One at a time is slower and legible: every row ends in a state you can read.
 *
 * A failure does not stop the run. The remaining staff are still sent, because
 * one person missing a Xero employee id is not a reason to hold up the other
 * fourteen — and the failed row keeps its reason on screen.
 */

type RowState = 'waiting' | 'queued' | 'sending' | 'sent' | 'failed' | 'skipped';

/**
 * How long to wait between one employee and the next.
 *
 * Xero allows 5 concurrent calls per tenant and caps calls per minute. One
 * employee is already a burst — a timesheet call plus one leave application per
 * leave day — and the server paces those internally. This is the gap BETWEEN
 * people, so fifteen employees arrive as a steady trickle rather than as one
 * long sprint at the limit.
 *
 * Two seconds is a guess at a courteous pace, not a documented requirement:
 * Xero imposes no restriction on sending several employees, and the API would
 * even take them in one array. Sending one at a time is a choice about being
 * able to read the result — every row ends in a state you can point at — and
 * the gap is cheap insurance on top of it. Thirty seconds for a whole pay run
 * is not a cost anyone notices.
 */
const GAP_BETWEEN_STAFF_MS = 2000;

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

interface BatchRow {
    readonly staffId: number;
    readonly name: string;
    readonly grandTotal: number;
    readonly uploadedAt: string | null;
    readonly approval: { readonly approvedAt: string; readonly approvedBy: string | null } | null;
    readonly blockedReason: string | null;
}

interface Batch {
    readonly from: IsoDate;
    readonly to: IsoDate;
    readonly xero: { readonly connected: boolean; readonly payrollEnabled: boolean };
    readonly staff: readonly BatchRow[];
}

interface Props {
    readonly staffIds: readonly number[];
    readonly from: IsoDate;
    /** Called once the run finishes, so the open tab reloads its figures. */
    readonly onSent: () => void;
}

export function XeroBatchSend({ staffIds, from, onSent }: Props): React.JSX.Element {
    const [batch, setBatch] = useState<Batch | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [ticked, setTicked] = useState<ReadonlySet<number>>(new Set());
    const [state, setState] = useState<ReadonlyMap<number, RowState>>(new Map());
    const [detail, setDetail] = useState<ReadonlyMap<number, string>>(new Map());
    const [running, setRunning] = useState(false);
    const [done, setDone] = useState(false);

    const load = useCallback(async (): Promise<void> => {
        try {
            const data = await apiFetch<Batch>(
                `/timesheets/payroll/batch?staffIds=${staffIds.join(',')}&from=${from}`,
            );
            setBatch(data);
            setError(null);
            // Ticked by default: everyone who can go and has not already been
            // sent. Someone re-sending a fortnight has to say so deliberately.
            setTicked(
                new Set(
                    data.staff
                        .filter((r) => r.blockedReason === null && r.uploadedAt === null)
                        .map((r) => r.staffId),
                ),
            );
            setState(new Map());
            setDetail(new Map());
            setDone(false);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : String(err));
        }
    }, [staffIds, from]);

    useEffect(() => {
        void load();
    }, [load]);

    function setRow(id: number, next: RowState, text?: string): void {
        setState((m) => new Map(m).set(id, next));
        if (text !== undefined) {
            setDetail((m) => new Map(m).set(id, text));
        }
    }

    async function run(): Promise<void> {
        if (batch === null) {
            return;
        }
        setRunning(true);
        const queue = batch.staff.filter((r) => ticked.has(r.staffId));
        // Everyone in the run is marked up front, so the office can see the
        // whole queue rather than watching rows light up one at a time with no
        // sense of what is still to come.
        for (const row of queue) {
            setRow(row.staffId, 'queued');
        }
        for (let i = 0; i < queue.length; i += 1) {
            const row = queue[i];
            if (row === undefined) {
                continue;
            }
            if (i > 0) {
                await sleep(GAP_BETWEEN_STAFF_MS);
            }
            setRow(row.staffId, 'sending');
            try {
                const result = await apiFetch<{ message: string; leaveFailures: readonly string[] }>(
                    '/timesheets/payroll/send-to-xero',
                    { method: 'POST', body: JSON.stringify({ staffId: row.staffId, from }) },
                );
                // A timesheet that landed but with leave days rejected is not a
                // success to gloss over — it is reported as a failure so nobody
                // assumes the leave went across.
                if (result.leaveFailures.length > 0) {
                    setRow(row.staffId, 'failed', result.leaveFailures.join('; '));
                } else {
                    setRow(row.staffId, 'sent', result.message);
                }
            } catch (err: unknown) {
                setRow(row.staffId, 'failed', err instanceof Error ? err.message : String(err));
            }
        }
        setRunning(false);
        setDone(true);
        onSent();
    }

    if (error !== null) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (batch === null) {
        return <Box sx={{ minHeight: 120 }} />;
    }

    const blockedGlobally = !batch.xero.connected
        ? 'Xero is not connected. Connect it on the Company Details screen.'
        : !batch.xero.payrollEnabled
          ? 'Xero is connected without payroll access. Reconnect Xero and approve payroll to enable sending.'
          : null;

    const tickedCount = ticked.size;
    const sentCount = [...state.values()].filter((v) => v === 'sent').length;
    const failedCount = [...state.values()].filter((v) => v === 'failed').length;

    return (
        <Box>
            {blockedGlobally !== null ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {blockedGlobally}
                </Alert>
            ) : null}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {dmy(batch.from)} – {dmy(batch.to)}. Each person is sent as a draft timesheet, one after another.
                Leave is sent separately as leave applications. Staff are sent one at a time, a couple of seconds
                apart, to stay well inside Xero&apos;s rate limits. Only approved fortnights can be sent — approve
                each person on their own tab first.
            </Typography>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox">
                            <Checkbox
                                size="small"
                                disabled={running}
                                checked={tickedCount > 0 && tickedCount === batch.staff.filter((r) => r.blockedReason === null).length}
                                indeterminate={tickedCount > 0 && tickedCount < batch.staff.filter((r) => r.blockedReason === null).length}
                                onChange={(e) => {
                                    setTicked(
                                        e.target.checked
                                            ? new Set(
                                                  batch.staff
                                                      .filter((r) => r.blockedReason === null)
                                                      .map((r) => r.staffId),
                                              )
                                            : new Set(),
                                    );
                                }}
                            />
                        </TableCell>
                        <TableCell>Staff member</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {batch.staff.map((row) => {
                        const rowState = state.get(row.staffId) ?? 'waiting';
                        const text = detail.get(row.staffId);
                        return (
                            <TableRow key={row.staffId} hover>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        disabled={running || row.blockedReason !== null}
                                        checked={ticked.has(row.staffId)}
                                        onChange={(e) => {
                                            setTicked((prev) => {
                                                const next = new Set(prev);
                                                if (e.target.checked) {
                                                    next.add(row.staffId);
                                                } else {
                                                    next.delete(row.staffId);
                                                }
                                                return next;
                                            });
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {row.name}
                                    </Typography>
                                    {row.blockedReason !== null ? (
                                        <Typography variant="caption" color="warning.main">
                                            {row.blockedReason}
                                        </Typography>
                                    ) : row.approval !== null ? (
                                        <Typography variant="caption" color="success.main">
                                            Approved {dmy(row.approval.approvedAt.slice(0, 10))}
                                            {row.approval.approvedBy === null
                                                ? ''
                                                : ` by ${row.approval.approvedBy}`}
                                        </Typography>
                                    ) : row.uploadedAt !== null && rowState === 'waiting' ? (
                                        <Typography variant="caption" color="text.secondary">
                                            Already sent {dmy(row.uploadedAt.slice(0, 10))} — ticking sends another
                                            draft
                                        </Typography>
                                    ) : null}
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {row.grandTotal.toFixed(2)}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 340 }}>
                                    {rowState === 'queued' ? (
                                        <Chip size="small" variant="outlined" label="Queued" />
                                    ) : rowState === 'sending' ? (
                                        <Chip size="small" color="info" label="Sending…" />
                                    ) : rowState === 'sent' ? (
                                        <Chip
                                            size="small"
                                            color="success"
                                            icon={<CheckCircleIcon />}
                                            label="Sent"
                                        />
                                    ) : rowState === 'failed' ? (
                                        <Stack spacing={0.25}>
                                            <Chip size="small" color="error" icon={<ErrorIcon />} label="Failed" />
                                            <Typography variant="caption" color="error.main">
                                                {text ?? ''}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" color="text.disabled">
                                            {ticked.has(row.staffId) ? 'Ready' : 'Not ticked'}
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 2 }} flexWrap="wrap">
                <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    disabled={running || tickedCount === 0 || blockedGlobally !== null}
                    onClick={() => void run()}
                >
                    {running
                        ? 'Sending…'
                        : `Send ${String(tickedCount)} staff member${tickedCount === 1 ? '' : 's'} to Xero`}
                </Button>
                {done ? (
                    <Typography variant="body2">
                        {sentCount} sent{failedCount > 0 ? `, ${String(failedCount)} failed` : ''}.
                    </Typography>
                ) : null}
                {done ? (
                    <Button size="small" onClick={() => void load()}>
                        Refresh
                    </Button>
                ) : null}
            </Stack>
        </Box>
    );
}
